import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Box, Button, Card, CircularProgress, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { getLiveCameraDailyReport, getLiveDailyReportTimezone } from '../api/liveDailyReports';
import { getLiveDailyReportChartData } from '../utils/liveDailyReportChart';

const COLORS = ['#10C9C9', '#9794FF', '#FFB55B', '#5CA7FF', '#EF83BC', '#83D688'];
const TEXT = '#B5D7FF';

export default function LiveReportBarChart({ cameras, loadingCameras }) {
  const [cameraId, setCameraId] = useState('');
  const [report, setReport] = useState(null);
  const [timezone, setTimezone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const timezoneId = localStorage.getItem('timezone_id');

  useEffect(() => {
    setCameraId((current) => cameras.some((camera) => camera.id === current) ? current : cameras[0]?.id || '');
  }, [cameras]);

  useEffect(() => {
    if (!cameraId) return undefined;
    let active = true;
    let timer;
    let pending = false;
    const controller = new AbortController();
    setReport(null);
    setTimezone('');
    setLoading(true);
    setError('');
    const refresh = async (name) => {
      if (pending || !active) return;
      pending = true;
      try {
        const result = await getLiveCameraDailyReport({ cameraId, timezone: name, signal: controller.signal });
        if (active) { setReport(result); setError(''); }
      } catch {
        if (active) setError('Unable to refresh today’s zone report. Please retry.');
      } finally {
        pending = false;
        if (active) setLoading(false);
      }
    };
    getLiveDailyReportTimezone(timezoneId, { signal: controller.signal }).then((name) => {
      if (!active) return;
      setTimezone(name);
      refresh(name);
      timer = setInterval(() => refresh(name), 30000);
    }).catch(() => {
      if (active) {
        setError('Unable to resolve the client timezone. Please check the configured timezone and retry.');
        setLoading(false);
      }
    });
    return () => { active = false; clearInterval(timer); controller.abort(); };
  }, [cameraId, timezoneId, retry]);

  const zones = report?.zones || [];
  const data = getLiveDailyReportChartData(report || {});
  const maximum = Math.max(8, ...data.flatMap((bucket) => bucket.values.map((value) => value.people || 0)));
  const ceiling = Math.ceil(maximum / 4) * 4;
  const left = 80;
  const top = 35;
  const plotHeight = 300;
  const baseline = top + plotHeight;
  const columnWidth = 56;
  const width = left + 24 * columnWidth + 20;
  const barWidth = Math.min(18, (columnWidth - 12) / Math.max(1, zones.length));
  const generatedAt = Date.parse(report?.generated_at);
  const updated = timezone && Number.isFinite(generatedAt) ? new Intl.DateTimeFormat('en-IN', {
    timeZone: timezone, day: 'numeric', month: 'short', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  }).format(new Date(generatedAt)) : '';

  return (
    <Card variant="outlined" sx={{ mb: 3, p: { xs: 2, md: 2.5 }, bgcolor: '#1C2434', color: TEXT, borderColor: '#354158', borderRadius: 1, boxShadow: 'none' }}>
      <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="flex-start">
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#E0EEFF' }}>Today’s live zone report</Typography>
          <Typography variant="caption">24 hourly groups{report?.date ? ` · ${report.date}` : ''}{timezone ? ` · ${timezone}` : ''}</Typography>
        </Box>
        <TextField select size="small" label="Camera" value={cameras.some((camera) => camera.id === cameraId) ? cameraId : ''} disabled={loadingCameras || !cameras.length}
          onChange={(event) => setCameraId(event.target.value)}
          sx={{ minWidth: { xs: 130, sm: 165 }, '& .MuiInputBase-root': { color: '#E0EEFF', fontSize: 12 }, '& .MuiInputLabel-root': { color: TEXT }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#40506C' }, '& .MuiSvgIcon-root': { color: '#E0EEFF' } }}>
          {cameras.map((camera) => <MenuItem key={camera.id} value={camera.id}>{camera.name}</MenuItem>)}
        </TextField>
      </Stack>
      <Typography variant="caption" sx={{ display: 'block', mt: 2, fontSize: 10 }}>
        Refreshes every 30 seconds{updated ? ` · Last updated ${updated}` : ''}
      </Typography>
      {(loading || loadingCameras) && <Stack direction="row" spacing={1} alignItems="center" role="status" sx={{ mt: 1 }}>
        <CircularProgress size={16} /><Typography variant="caption">Loading today’s report...</Typography>
      </Stack>}
      {error && <Alert severity="error" sx={{ mt: 1 }} action={<Button color="inherit" onClick={() => setRetry((value) => value + 1)}>Retry</Button>}>{error}</Alert>}
      {report && !zones.length && <Alert severity="info" sx={{ mt: 1 }}>No zones available for this camera.</Alert>}
      <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
        {zones.map((zone, index) => <Stack key={zone.id} direction="row" spacing={0.75} alignItems="center">
          <Box sx={{ width: 8, height: 8, borderRadius: 0.25, bgcolor: COLORS[index % COLORS.length] }} />
          <Typography variant="caption" sx={{ fontSize: 11 }}>{zone.name}</Typography>
        </Stack>)}
      </Stack>
      <Typography variant="caption" sx={{ display: 'block', mt: 1, fontSize: 11 }}>Average people present (people)</Typography>
      <Box sx={{ overflowX: 'auto', mt: 1 }}>
        <Box component="svg" viewBox={`0 0 ${width} 430`} role="img" aria-label="Today’s hourly zone occupancy, from midnight through the current hour"
          sx={{ display: 'block', width: '100%', minWidth: 850 }}>
          <title>Today’s live zone report: hourly average people present</title>
          {data.filter((bucket) => bucket.partial).map((bucket) => <rect key={bucket.hour}
            x={left + bucket.hour * columnWidth} y={top - 10} width={columnWidth} height={plotHeight + 24} fill="#10C9C9" opacity="0.08" />)}
          {Array.from({ length: 5 }, (_, index) => {
            const value = index * ceiling / 4;
            const y = baseline - index * plotHeight / 4;
            return <g key={index}>
              <line x1={left} x2={width - 20} y1={y} y2={y} stroke="#354052" />
              <text x={left - 16} y={y + 5} fill={TEXT} textAnchor="end" fontSize="14">{value}</text>
            </g>;
          })}
          {data.map((bucket) => <g key={bucket.hour}>
            {bucket.future ? <text x={left + (bucket.hour + 0.5) * columnWidth} y={baseline - 9} fill="#72849D" textAnchor="middle" fontSize="16">–</text>
              : zones.map((zone, index) => {
                const value = bucket.values[index];
                const x = left + (bucket.hour + 0.5) * columnWidth + (index - zones.length / 2) * barWidth;
                const height = (value.people || 0) / ceiling * plotHeight;
                const tooltip = `${zone.name} · ${bucket.label}${bucket.partial ? ' · Partial hour' : ''}\n${value.average === null ? 'Unavailable / zone inactive' : `Average: ${value.average.toFixed(2)} people\nCoverage: ${Number.isFinite(value.coverage) ? `${value.coverage.toFixed(1)}%` : 'Unavailable'}\nObserved time: ${(value.coveredSeconds / 60).toFixed(1)} minutes`}`;
                return <g key={zone.id}>
                  <title>{tooltip}</title>
                  {value.people === null ? <text x={x + barWidth / 2} y={baseline - 9} textAnchor="middle" fill="#72849D" fontSize="12">×</text>
                    : <rect x={x} y={baseline - Math.max(height, 1)} width={Math.max(1, barWidth - 2)} height={Math.max(height, 1)} rx="2" fill={COLORS[index % COLORS.length]} />}
                </g>;
              })}
            <text x={left + (bucket.hour + 0.5) * columnWidth} y={baseline + 36} fill={TEXT} textAnchor="middle" fontSize="13">{bucket.label}</text>
            {bucket.partial && <text x={left + (bucket.hour + 0.5) * columnWidth} y={baseline + 57} fill="#10E5E5" textAnchor="middle" fontSize="11">Partial</text>}
          </g>)}
          <text x={left + 12 * columnWidth} y="410" fill={TEXT} textAnchor="middle" fontSize="16">Hour of day{timezone ? ` (${timezone})` : ''}</text>
        </Box>
      </Box>
      <Typography variant="caption" sx={{ display: 'block', mt: 1, fontSize: 10 }}>
        Each bar is average occupancy during observed time in that hour. × = unavailable / zone inactive; – = future hour. Zero means observed zero. Hover over a bar for the measured average and coverage.
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', mt: 1, fontSize: 10 }}>
        People counts: positive averages below 1 display as 1; above 1, fractions up to 0.5 round down and above 0.5 round up. Raw measurements stay unchanged.
      </Typography>
    </Card>
  );
}

LiveReportBarChart.propTypes = { cameras: PropTypes.array.isRequired, loadingCameras: PropTypes.bool };
